import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import api from '../api';

// Cache the catalog across openings (client-side search over the 73 products).
let productsCache = null;

const SearchModal = ({ open, onClose }) => {
  const { t, lang, loc } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState(productsCache || []);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    // focus the input when the modal opens
    setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    if (!productsCache) {
      setLoading(true);
      api.getProducts()
        .then((res) => {
          productsCache = Array.isArray(res.data) ? res.data : [];
          setProducts(productsCache);
        })
        .catch(() => setProducts([]))
        .finally(() => setLoading(false));
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) => {
      const hay = [
        p.name, p.name_en, p.short_description, p.short_description_en,
        p.description, p.description_en, p.category_name, p.macro_section, p.slug,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    }).slice(0, 12);
  }, [query, products]);

  const goTo = (p) => {
    onClose();
    navigate(`/prodotto/${p.slug}`);
  };

  if (!open) return null;

  return (
    <div className="cp-search-overlay" onClick={onClose} role="dialog" aria-modal="true"
         aria-label={lang === 'en' ? 'Product search' : 'Ricerca prodotti'}>
      <div className="cp-search-box" onClick={(e) => e.stopPropagation()}>
        <div className="cp-search-input-row">
          <FiSearch size={20} aria-hidden="true" className="cp-search-input-icon" />
          <input
            ref={inputRef}
            className="cp-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && results.length) goTo(results[0]); }}
            placeholder={t('search.placeholder')}
            aria-label={t('search.placeholder')}
          />
          <button className="cp-search-close" onClick={onClose}
                  aria-label={lang === 'en' ? 'Close search' : 'Chiudi ricerca'}>
            <FiX size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="cp-search-results">
          {loading && <div className="cp-search-hint">{lang === 'en' ? 'Loading catalog…' : 'Caricamento catalogo…'}</div>}
          {!loading && !query.trim() && (
            <div className="cp-search-hint">{t('search.hint')}</div>
          )}
          {!loading && query.trim() && results.length === 0 && (
            <div className="cp-search-hint">{t('search.noResults')}</div>
          )}
          {results.map((p) => (
            <button key={p.id} className="cp-search-result" onClick={() => goTo(p)}>
              <div className="cp-search-result-img">
                {p.image
                  ? <img src={p.image} alt="" loading="lazy" />
                  : <FiSearch aria-hidden="true" />}
              </div>
              <div className="cp-search-result-text">
                <span className="cp-search-result-name">{loc(p, 'name')}</span>
                {(p.category_name || p.macro_section) && (
                  <span className="cp-search-result-cat">{p.category_name || p.macro_section}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
